import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import {
  CHANNEL_IDS,
  CHANNEL_CONFIG,
  CHANNEL_LABEL_KEYS,
  CHANNEL_DESCRIPTION_KEYS,
  PRIORITY_OPTIONS,
  PRIORITY_CONFIG,
  PRIORITY_LABEL_KEYS,
  TAP_DESTINATION_MAP,
} from "../constants";
import type {
  NotificationTemplate,
  ChannelId,
  TemplatePriority,
} from "../types";

interface TemplateListProps {
  templates: NotificationTemplate[];
  loading: boolean;
  onEdit: (template: NotificationTemplate) => void;
  onDeactivate: (name: string, locale: string) => void;
  onReactivate: (template: NotificationTemplate) => void;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function TemplateList({ templates, loading, onEdit, onDeactivate, onReactivate }: TemplateListProps) {
  // `t` is used below as the per-row template item; alias the translator to avoid shadowing.
  const { t: translate } = useLocale();
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState<ChannelId | "ALL">("ALL");
  const [priorityFilter, setPriorityFilter] = useState<TemplatePriority | "ALL">("ALL");
  const [showDeactivated, setShowDeactivated] = useState(false);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (!showDeactivated && !t.isActive) return false;
      if (channelFilter !== "ALL" && t.channelId !== channelFilter) return false;
      if (priorityFilter !== "ALL" && t.priority !== priorityFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return t.name.toLowerCase().includes(q) || t.title.toLowerCase().includes(q);
      }
      return true;
    });
  }, [templates, search, channelFilter, priorityFilter, showDeactivated]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-sm text-muted-foreground">{translate("notifications.list.loading")}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Info banner */}
      <div className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-3">
        <p className="text-sm text-blue-800 dark:text-blue-300">
          <span className="font-medium">{translate("notifications.list.infoStrong")}</span>{" "}
          {translate("notifications.list.infoText")}
        </p>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={translate("notifications.list.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value as ChannelId | "ALL")}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{translate("notifications.list.allChannels")}</option>
          {CHANNEL_IDS.map((ch) => (
            <option key={ch} value={ch}>
              {CHANNEL_CONFIG[ch].emoji} {translate(CHANNEL_LABEL_KEYS[ch])}
            </option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as TemplatePriority | "ALL")}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="ALL">{translate("notifications.list.allPriorities")}</option>
          {PRIORITY_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {translate(PRIORITY_LABEL_KEYS[p])}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={showDeactivated}
            onChange={(e) => setShowDeactivated(e.target.checked)}
            className="rounded border-border"
          />
          {translate("notifications.list.showDeactivated")}
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-muted-foreground">{translate("notifications.list.noMatch")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.list.colName")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.list.colTitle")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.field.channel")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.field.priority")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.list.colDestination")}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground">{translate("notifications.list.colStatus")}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">{translate("notifications.list.colUpdated")}</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">{translate("notifications.list.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((t) => {
                const isBuiltIn = !t.createdBy;
                const channelCfg = CHANNEL_CONFIG[t.channelId as ChannelId];
                const priorityCfg = PRIORITY_CONFIG[t.priority as TemplatePriority];
                const destination = TAP_DESTINATION_MAP[t.name];

                return (
                  <tr
                    key={t._id}
                    className={`hover:bg-muted/50 ${!t.isActive ? "opacity-60" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
                        {t.name}
                      </code>
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-foreground">{t.title}</td>
                    <td className="px-4 py-3">
                      {channelCfg ? (
                        <span
                          className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                          title={translate(CHANNEL_DESCRIPTION_KEYS[t.channelId as ChannelId])}
                        >
                          {channelCfg.emoji} {translate(CHANNEL_LABEL_KEYS[t.channelId as ChannelId])}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t.channelId}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {priorityCfg ? (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${priorityCfg.bg} ${priorityCfg.color}`}>
                          {translate(PRIORITY_LABEL_KEYS[t.priority as TemplatePriority])}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">{t.priority}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {destination ? (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          📱 {translate(destination.labelKey)}
                        </span>
                      ) : (
                        <span className="text-xs italic text-muted-foreground">{translate("notifications.list.destinationDefault")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {t.isActive ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          {translate("notifications.badge.active")}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          {translate("notifications.badge.inactive")}
                        </span>
                      )}
                      {isBuiltIn && (
                        <span className="mt-0.5 block text-[10px] text-muted-foreground">{translate("notifications.badge.builtIn")}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{timeAgo(t.updatedAt)}</td>
                    <td className="space-x-2 px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onEdit(t)}
                        className="text-xs font-medium text-primary hover:text-primary/80"
                      >
                        {translate("common.edit")}
                      </button>
                      {t.isActive ? (
                        <button
                          type="button"
                          onClick={() => onDeactivate(t.name, t.locale)}
                          className="text-xs font-medium text-destructive hover:text-destructive/80"
                        >
                          {translate("notifications.action.deactivate")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onReactivate(t)}
                          className="text-xs font-medium text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                        >
                          {translate("notifications.action.reactivate")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
